import React, { useState } from 'react';
import {
  Box,
  List,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { ConversationListItem } from './ConversationListItem';
import { CreateConversationDialog } from './CreateConversationDialog';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import type { Conversation } from '../../types/message';

/**
 * ConversationList Component
 *
 * Left sidebar showing all conversations with:
 * - Search/filter functionality
 * - Tabs for All/Direct/Groups
 * - New conversation button
 * - List of conversations
 */
export const ConversationList: React.FC = () => {
  const { authState } = useAuth();
  const user = authState.user;
  const { conversations, activeConversationId, setActiveConversation, loading } = useMessaging();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState<'all' | 'direct' | 'group'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  if (!user) return null;

  // Filter conversations
  const filteredConversations = conversations.filter((conv: Conversation) => {
    // Filter by type
    if (filterTab === 'direct' && conv.type !== 'direct') return false;
    if (filterTab === 'group' && conv.type !== 'group') return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();

      // Search in conversation name
      if (conv.name?.toLowerCase().includes(query)) return true;

      // Search in last message
      if (conv.lastMessage?.text.toLowerCase().includes(query)) return true;

      // Search in participant IDs (in production, search user names)
      if (conv.participants.some(id => id.toLowerCase().includes(query))) return true;

      return false;
    }

    return true;
  });

  // Sort by last activity
  const sortedConversations = [...filteredConversations].sort(
    (a, b) => b.lastActivity.getTime() - a.lastActivity.getTime()
  );

  // Filter out archived conversations (unless explicitly shown)
  const visibleConversations = sortedConversations.filter(
    (conv) => !conv.memberInfo[user.uid]?.archived
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'all' | 'direct' | 'group'): void => {
    setFilterTab(newValue);
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Messages</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setCreateDialogOpen(true)}
          >
            New
          </Button>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Filter Tabs */}
        <Tabs value={filterTab} onChange={handleTabChange} sx={{ mt: 1 }}>
          <Tab label="All" value="all" />
          <Tab label="Direct" value="direct" />
          <Tab label="Groups" value="group" />
        </Tabs>
      </Box>

      {/* Conversations List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleConversations.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? 'No conversations found'
                : 'No conversations yet. Start a new one!'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {visibleConversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                isActive={activeConversationId === conversation.id}
                onClick={() => setActiveConversation(conversation.id)}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Create Conversation Dialog */}
      <CreateConversationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
};
